import { useCallback, useEffect, useState } from 'react';
import { APP_NAME, STAFF_DISPATCH } from 'shared';
import {
  api,
  ApiError,
  hasToken,
  isViewingAs,
  logout,
  stopViewAs,
  type Session,
} from './api';
import {
  haptic,
  isTelegram,
  notify as notifyHaptic,
  parseStartParam,
} from './telegram';
import { connect, disconnect, useLiveRefresh, useRealtime } from './realtime';
import { AuctionBar } from './AuctionBar';
import { clearAuctions, loadAuctions, wireAuctions } from './auctions';
import { bumpFreshOrders, markOrdersSeen, useFreshOrders } from './freshOrders';
import Feed from './pages/Feed';
import CargoDetail from './pages/CargoDetail';
import Modal from './Modal';
import { useSwipe } from './useSwipe';
import Publish from './pages/Publish';
import Orders from './pages/Orders';
import Profile from './pages/Profile';
import MyCargos from './pages/MyCargos';
import Trucks from './pages/Trucks';
import Login from './pages/Login';
import Admin from './pages/Admin';
import Company from './pages/Company';
import Money from './pages/Money';
import Analytics from './pages/Analytics';
import Dispatch from './pages/Dispatch';
import Welcome from './pages/Welcome';
import PendingApproval from './PendingApproval';
import Icon from './Icon';
import Support from './Support';
import TopBar from './TopBar';
import MobileNav from './MobileNav';
import Notifications from './Notifications';
import Tour from './Tour';
import { tourSeen } from './tourState';
import { Toasts } from './toast';
import { MoneyProvider } from './money';
import { toggleNavWidth, useNavWide } from './navWidth';
import {
  ADMIN_GROUPS,
  ADMIN_TAB_LABEL,
  type AdminTab,
} from './pages/AdminNav';
import Pulse from './Pulse';
import { useT } from './i18n';
import { useFeatures } from './features';
import { navFor, SCREEN_TITLE, type ScreenName } from './nav';

export type Screen = { name: ScreenName; editCargoId?: string; draftId?: string };

type Status = 'checking' | 'anonymous' | 'ready';

export default function App() {
  // Все хуки — до любых ранних return: ниже по коду есть выходы на экран
  // загрузки и форму входа, и вызов хука после них ломает React (#310).
  const { t: translate } = useT();
  const features = useFeatures();

  const [status, setStatus] = useState<Status>('checking');
  const [session, setSession] = useState<Session['user'] | null>(null);
  /**
   * Чужими глазами. Признак берётся из двух мест: сервер говорит о пометке
   * в токене, браузер помнит отложенный токен администратора. Достаточно
   * одного — сервер отвечает не сразу, а отложенный токен переживает
   * перезагрузку.
   */
  const [viewingAs, setViewingAs] = useState(isViewingAs());
  const [authError, setAuthError] = useState<ApiError | null>(null);
  const [screen, setScreen] = useState<Screen>({ name: 'feed' });
  /*
   * Обучение по площадке. Показывается один раз — сразу после выбора
   * профиля деятельности, пока человек ещё не начал ничего делать и
   * готов смотреть. Повторно запускается из профиля.
   */
  const [tour, setTour] = useState(false);
  /*
   * Открытая заявка — окно поверх текущего экрана, а не отдельный экран.
   *
   * Человек листает ленту, заглядывает в карточку и возвращается. Отдельный
   * экран каждый раз перезагружал ленту и терял позицию прокрутки — вместе
   * с местом, на котором остановился.
   */
  const [cargoId, setCargoId] = useState<string | null>(null);
  /*
   * Валюта отображения. Живёт здесь, а не в профиле: её читает каждый
   * экран с суммами, а профиль — лишь одно из мест, где её меняют.
   * Приходит из `/me`, поэтому у входа по Telegram появляется не сразу —
   * до этого суммы показываются в валюте сделки, что и так верно.
   */
  const [displayCurrency, setDisplayCurrency] = useState<string | null>(null);
  /*
   * Раздел админки живёт здесь, а не внутри самой админки: его пункты
   * стоят в общем боковом меню, и владеть выбором должно то, что его
   * рисует. Два меню рядом — одно приложения, второе админки — читались
   * как ошибка вёрстки, и были ею.
   */
  const [adminTab, setAdminTab] = useState<AdminTab | null>(null);
  const navWide = useNavWide();

  // Список разделов собирается в одном месте на всё: боковую панель, нижнее
  // меню и свайпы между страницами. Две копии однажды разъехались —
  // «Аналитика» осталась только в панели.
  const groups = navFor({
    features,
    isAdmin: Boolean(session?.isAdmin),
    isStaff: STAFF_DISPATCH.includes(session?.staffRole ?? 'NONE'),
    profileType: session?.profileType ?? null,
  });

  const openCargo = useCallback((id: string) => {
    haptic('light');
    setCargoId(id);
  }, []);

  /*
   * Стека экранов больше нет.
   *
   * Все разделы — вкладки одного уровня, а заявка открывается окном поверх
   * текущего экрана. Возвращаться стало некуда, и стек с кнопкой «назад»
   * превратились в лишнюю сущность, которую надо было держать в согласии
   * с системной кнопкой Telegram.
   */
  const goTab = useCallback((name: Screen['name']) => {
    setScreen({ name } as Screen);
  }, []);

  /*
   * Чужими глазами обучение не показываем: администратор разбирает чужую
   * проблему, а не знакомится с площадкой, и отметка «прошёл» осталась бы
   * на его устройстве вместо устройства пользователя.
   */
  useEffect(() => {
    if (session?.profileType && !viewingAs && !tourSeen()) setTour(true);
  }, [session?.profileType, viewingAs]);

  /**
   * С какого экрана начинать.
   *
   * Лента — правильное начало только для того, кто сам выбирает грузы.
   * Логист заходит смотреть отклики на свои заявки, а наёмному водителю
   * рейсы назначает диспетчер: лента ему не нужна вовсе, и первый экран
   * он каждый раз пролистывал мимо.
   */
  const startScreen = useCallback((user: Session['user']): Screen['name'] => {
    /*
     * В Telegram всегда начинаем с ленты грузов.
     *
     * Mini App открывают с телефона и почти всегда с одним вопросом —
     * «что везти». Роль тут решает меньше, чем место: диспетчер за
     * рабочим столом распределяет технику, он же в дороге смотрит грузы.
     */
    if (isTelegram) return 'feed';

    if (user.role === 'LOGIST') return 'mycargos';
    if (!user.canTakeCargo) return 'orders';
    // Диспетчер парка начинает с машин: его работа — распределить технику,
    // а не выбрать себе груз. Проверяем и профиль: раздел «Машины» виден
    // только перевозчику и экспедитору, и отправлять туда заказчика значит
    // открывать приложение на пустом месте.
    if (
      user.managesCompany &&
      (user.profileType === 'CARRIER' || user.profileType === 'FORWARDER')
    ) {
      return 'trucks';
    }
    return 'feed';
  }, []);

  const applyStartParam = useCallback(
    (user: Session['user']) => {
      const start = parseStartParam();
      if (start?.screen === 'cargo' && start.id) setCargoId(start.id);
      else if (start?.screen === 'offers' && start.id) setCargoId(start.id);
      else if (start?.screen === 'orders') setScreen({ name: 'orders' });
      // Ссылка из бота на быструю заявку: открываем форму публикации
      // с подставленным черновиком.
      else if (start?.screen === 'newcargo' && start.id) {
        setScreen({ name: 'publish', draftId: start.id });
      }
      // Ссылка из уведомления сильнее роли: человек шёл в конкретное место.
      else setScreen({ name: startScreen(user) });
    },
    [startScreen],
  );

  /**
   * Три пути входа, в порядке приоритета:
   *   1. Telegram — подпись initData, происходит молча;
   *   2. сохранённый токен из прошлой веб-сессии;
   *   3. форма логина.
   */
  useEffect(() => {
    /**
     * Сессия по сохранённому токену. `true` — вошли; `false` — токен
     * больше не годится; исключение — только «ждёт допуска», его вызывающий
     * показывает отдельным экраном.
     */
    async function loadMe(): Promise<boolean> {
      try {
        const me = await api.me();
        const user: Session['user'] = {
          id: me.id,
          firstName: me.firstName,
          lastName: me.lastName,
          login: me.login,
          phone: me.phone,
          role: me.role,
          profileType: me.profileType,
          isAdmin: me.isAdmin,
          staffRole: me.staffRole,
          hasPassword: me.hasPassword,
          needsOnboarding: !me.phone,
          canTakeCargo: me.canTakeCargo,
          managesCompany: me.managesCompany,
        };
        setSession(user);
        setViewingAs(isViewingAs() || Boolean(me.viewedByAdmin));
        setDisplayCurrency(me.displayCurrency);
        applyStartParam(user);
        setStatus('ready');
        return true;
      } catch (e) {
        if ((e as ApiError).reason === 'PENDING_APPROVAL') throw e;
        return false;
      }
    }

    async function boot() {
      /*
       * Чужими глазами в Telegram входим по токену просмотра, а не по
       * initData: подпись Telegram всегда даёт свою учётную запись и молча
       * отменила бы просмотр. Хуже того, запрос входа уходил с токеном
       * просмотра, сервер отбивал его как «запись чужими глазами» (403),
       * и администратор застревал на экране ошибки без кнопки назад.
       *
       * Токен просмотра живёт час и может быть отозван — тогда просмотр
       * заканчиваем сами и входим обычным путём, а не показываем ошибку.
       */
      if (isTelegram && isViewingAs()) {
        if (hasToken() && (await loadMe().catch(() => false))) return;
        stopViewAs();
        setViewingAs(false);
      }

      if (isTelegram) {
        try {
          const res = await api.loginTelegram();
          setSession(res.user);
          applyStartParam(res.user);
          setStatus('ready');
          return;
        } catch (e) {
          setAuthError(e as ApiError);
          setStatus('anonymous');
          return;
        }
      }

      if (hasToken()) {
        try {
          if (await loadMe()) return;
        } catch (e) {
          /*
           * Деактивировали уже одобренного человека — токен ещё валиден,
           * а requireAuth отклоняет всё. Токен не стираем: разлогин здесь
           * увёл бы на форму входа и стёр бы память о том, что заявка
           * когда-то была одобрена — а как только доступ вернут, обычная
           * перезагрузка должна снова пройти /me без повторного входа.
           */
          setAuthError(e as ApiError);
          setStatus('anonymous');
          return;
        }
        // Токен протух — падаем на форму входа.
        logout();
      }

      setStatus('anonymous');
    }

    void boot();
  }, [applyStartParam]);

  // Окно заявки закрывает системная кнопка «назад» — этим теперь заведует
  // сам `Modal` через `useBackButton`, отдельная обвязка здесь не нужна.

  /*
   * Свайпы между разделами — «серфинг страниц».
   *
   * Порядок совпадает с меню: тот же список разделов, что видит человек,
   * листается влево-вправо. Только в Telegram и только на верхнем уровне:
   * при открытом окне заявки жест означал бы «назад», а в браузере
   * горизонтальный свайп занят самим браузером.
   */
  const swipeOrder = groups.flatMap((g) => g.items.map((i) => i.name));
  const tabAt = swipeOrder.indexOf(screen.name);

  const goSide = useCallback(
    (step: number) => {
      if (tabAt < 0) return;
      const next = swipeOrder[tabAt + step];
      if (next) goTab(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabAt, goTab],
  );

  useSwipe({
    enabled: isTelegram && !cargoId,
    onLeft: () => goSide(1),
    onRight: () => goSide(-1),
  });

  // Живое соединение поднимаем один раз после входа — оно общее
  // для всех экранов, каждый просто подписывается на нужные события.
  useEffect(() => {
    if (status !== 'ready') return;
    connect();
    return () => disconnect();
  }, [status]);

  /*
   * Свои торги подтягиваем при входе и при возвращении вкладки.
   *
   * События рассказывают об изменениях, но не о том, что было до
   * подключения: зашедший через час после ставки обязан увидеть таймер
   * сразу, а не после того, как кто-то перебьёт его цену. Отклик при
   * перебитии — только в Telegram и только на переход «вёл → перебили».
   */
  useEffect(() => {
    if (status !== 'ready') {
      clearAuctions();
      return;
    }
    wireAuctions(() => notifyHaptic('warning'));
    void loadAuctions();
  }, [status]);

  useLiveRefresh(() => {
    if (status === 'ready') void loadAuctions();
  }, 120_000);

  /*
   * События по рейсам поднимают значок сразу, не дожидаясь опроса.
   *
   * Три события, а не одно: рейс появился (приняли отклик), рейс сдвинулся
   * по статусу, рейс разорвали. Все три — повод заглянуть в раздел, и
   * различать их значком незачем: он отвечает только на «мне туда надо?».
   */
  useRealtime('offer:accepted', () => bumpFreshOrders());
  useRealtime('order:status', () => bumpFreshOrders());
  useRealtime('order:cancelled', () => bumpFreshOrders());

  const freshOrders = useFreshOrders();

  /*
   * Раздел открыт — значок гаснет.
   *
   * Именно при показе экрана, а не при нажатии на вкладку: попасть в
   * «Рейсы» можно и из уведомления, и по возврату из карточки, и значок,
   * переживший просмотр списка, читается как поломка.
   */
  useEffect(() => {
    if (status === 'ready' && screen.name === 'orders') markOrdersSeen();
  }, [status, screen.name]);

  if (status === 'checking') return <div className="empty">Загрузка…</div>;

  if (status === 'anonymous') {
    // Ждёт модерации или деактивирован — общая заглушка для Telegram и веба,
    // не считается «ошибкой входа» и до формы логина не доходит.
    if (authError?.reason === 'PENDING_APPROVAL') {
      return <PendingApproval onBack={() => setAuthError(null)} />;
    }

    // В Telegram форма логина бессмысленна: там вход должен быть автоматическим,
    // и если он не сработал — это ошибка конфигурации, её и показываем.
    if (isTelegram && authError) {
      return (
        // Шапки здесь нет, а «Закрыть» и «…» Telegram всё равно висят
        // поверх страницы — без отступа текст ошибки уходил под них.
        <div className="container tma-error">
          <div className="error">{authError.message}</div>

          {authError.reason === 'BAD_SIGNATURE' && (
            <div className="card">
              <h3>Как починить</h3>
              <div className="meta">
                На сервере прошит токен бота{' '}
                <strong>
                  {authError.expectedBot ? `@${authError.expectedBot}` : '—'}
                </strong>
                , а приложение открыто из другого. Совпасть должны оба:
                <br />
                <br />
                1. В @BotFather выберите этого бота → Bot Settings → Menu Button →
                укажите адрес Mini App.
                <br />
                2. Либо положите в <code>BOT_TOKEN</code> токен того бота, из
                которого открываете.
              </div>
            </div>
          )}

          {authError.reason === 'EXPIRED' && (
            <p className="meta">
              Закройте окно полностью и откройте заново из бота — Telegram выдаст
              свежую подпись.
            </p>
          )}
        </div>
      );
    }

    return (
      <Login
        onSuccess={() => {
          // Полная перезагрузка проще, чем протаскивать сессию через дерево:
          // вход случается один раз, лишний рендер здесь не жалко.
          window.location.reload();
        }}
      />
    );
  }

  // Профиль деятельности спрашиваем до первого экрана: от него зависит,
  // что человек увидит, и задавать этот вопрос позже уже поздно.
  if (session && !session.profileType) {
    return (
      <div className="auth-shell">
        <Welcome onDone={() => window.location.reload()} />
      </div>
    );
  }

  return (
    /*
     * Валюта отображения — сквозная: она нужна каждому экрану, где есть
     * суммы, а это почти каждый. Тянуть её пропсами через десяток
     * компонентов значило бы поменять их все ради одной строки.
     */
    <MoneyProvider displayCurrency={displayCurrency}>
      <Toasts />
      {tour && session?.profileType && (
        <Tour
          profile={session.profileType}
          onGoScreen={(name) => goTab(name as Screen['name'])}
          onClose={() => setTour(false)}
        />
      )}
      {/* Торги идут своим ходом и требуют ответа за секунды, поэтому
          полоска живёт рядом с навигацией, а не внутри экрана: человек
          в этот момент смотрит другую заявку или другой раздел. */}
      <AuctionBar onBid={openCargo} />
    <div className="shell">
      {/* Полоса на весь экран, а не значок в углу: перепутать, чей это
          экран, дороже, чем потерять полсантиметра высоты. */}
      {viewingAs && (
        <div className="viewas" role="status">
          <span>
            Вы смотрите глазами{' '}
            <strong>
              {[session?.firstName, session?.lastName].filter(Boolean).join(' ') ||
                session?.login ||
                'пользователя'}
            </strong>
            . Изменения запрещены.
          </span>
          <button
            onClick={() => {
              stopViewAs();
              // Перезагрузка, а не сброс состояния: чужими глазами успели
              // открыться экраны, которых у администратора нет, и разбирать
              // это состояние по кусочкам дороже, чем начать заново.
              window.location.reload();
            }}
          >
            Вернуться
          </button>
        </div>
      )}
      <nav className="tabs">
          <div className="brand">
            <span className="topbar__logo" aria-hidden>
              <Icon name="truck" size={18} />
            </span>
            <span className="brand-text">{APP_NAME}</span>
          </div>

          {/* Свернуть меню до значков или развернуть обратно. Выбор
              запоминается: кому-то важнее место под ленту, кому-то —
              чтобы разделы читались, и решать это за человека незачем. */}
          <button
            type="button"
            className="tabs__fold"
            title={navWide ? 'Свернуть меню' : 'Развернуть меню'}
            aria-label={navWide ? 'Свернуть меню' : 'Развернуть меню'}
            onClick={() => {
              haptic();
              toggleNavWidth();
            }}
          >
            <Icon name="arrow" size={16} />
          </button>
          {groups.map((g) => (
            <div key={g.title} className="tabs__group">
              <div className="tabs__title">{translate(g.title)}</div>
              {g.items.map((tab) => (
                <div key={tab.name}>
                  <button
                    data-tour={`nav-${tab.name}`}
                    className={screen.name === tab.name ? 'active' : ''}
                    onClick={() => goTab(tab.name)}
                  >
                    <Icon name={tab.icon} />
                    {translate(tab.label)}
                    {/* Цифра только у «Рейсов»: значок должен означать
                        «там появилось дело», а не украшать каждый пункт. */}
                    {tab.name === 'orders' && freshOrders > 0 && (
                      <span className="navbadge" aria-label={`Новых событий: ${freshOrders}`}>
                        {freshOrders > 99 ? '99+' : freshOrders}
                      </span>
                    )}
                  </button>

                  {/* Разделы админки — здесь же, вложенным списком.
                      Раскрываются, только когда админка открыта: держать
                      десять пунктов в меню постоянно значит мешать ими
                      всем, кто пришёл возить грузы. */}
                  {tab.name === 'admin' && screen.name === 'admin' && (
                    <div className="tabs__sub">
                      {ADMIN_GROUPS.map((sub) => (
                        <div key={sub.title}>
                          <div className="tabs__subtitle">{sub.title}</div>
                          {sub.items.map((t) => (
                            <button
                              key={t}
                              className={adminTab === t ? 'tabs__subitem active' : 'tabs__subitem'}
                              onClick={() => {
                                haptic();
                                setAdminTab(t);
                              }}
                            >
                              {ADMIN_TAB_LABEL[t]}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
      </nav>

      {/* Шапка и содержимое — одна колонка раскладки. Без обёртки они
          становятся отдельными ячейками сетки и разъезжаются. */}
      <div className="content">
      {/* В Telegram верхнюю панель не рисуем: там уже висят «Закрыть»,
          свернуть и «…», и любая своя шапка лезет под них. Название раздела
          показывает appbar, переключатели уехали в меню «Ещё». */}
      {session && !isTelegram && (
        <TopBar
          user={session}
          onProfile={() => goTab('profile')}
          onOpenCargo={openCargo}
          onOpenOrders={() => goTab('orders')}
        />
      )}

      <main className="container">
        {/* Кнопки «назад» здесь больше нет: возвращаться некуда — все
            разделы одного уровня, а заявка закрывается своим окном.
            В Telegram нет верхней панели, поэтому счётчики и колокольчик
            встают сюда, как в мессенджерах. */}
        {isTelegram && session && (
          <header className="appbar">
            {/* Название раздела встаёт в полосу системных кнопок Telegram —
                ровно между «Закрыть» и «…». Место там пустует, а заголовок
                оказывается там, где его ищут по привычке из мессенджеров. */}
            <span className="appbar__title">
              {translate(SCREEN_TITLE[screen.name])}
            </span>

            {/* Счётчики — по центру строки ниже. Колокольчик справа
                позиционируется поверх, чтобы не сдвигать их с середины. */}
            <div className="appbar__row">
              <Pulse />
              <Notifications
                onOpenCargo={openCargo}
                onOpenOrders={() => goTab('orders')}
              />
            </div>
          </header>
        )}

        {screen.name === 'feed' && (
          <Feed
            onOpen={openCargo}
            onEdit={(editCargoId) => setScreen({ name: 'publish', editCargoId })}
          />
        )}
        {screen.name === 'publish' && (
          <Publish
            // key: без него переход «правка → новая заявка» переиспользует
            // компонент вместе с уже заполненными полями прошлой заявки.
            key={screen.editCargoId ?? screen.draftId ?? 'new'}
            editId={screen.editCargoId}
            draftId={screen.draftId}
            onPublished={() => goTab('mycargos')}
          />
        )}
        {screen.name === 'mycargos' && (
          <MyCargos onOpen={openCargo} />
        )}
        {screen.name === 'trucks' && <Trucks onOpenCargo={openCargo} />}
        {screen.name === 'orders' && <Orders />}
        {screen.name === 'company' && <Company />}
        {screen.name === 'money' && <Money role={session?.role ?? 'DRIVER'} />}
        {screen.name === 'analytics' && (
          <Analytics isAdmin={Boolean(session?.isAdmin)} />
        )}
        {screen.name === 'profile' && <Profile />}
        {screen.name === 'dispatch' &&
          STAFF_DISPATCH.includes(session?.staffRole ?? 'NONE') && (
            <Dispatch
              isAdmin={Boolean(session?.isAdmin)}
              onOpenCargo={openCargo}
            />
          )}
        {screen.name === 'admin' && session?.isAdmin && (
          <Admin
            tab={adminTab}
            onTab={setAdminTab}
            onOpenDraft={(draftId) => setScreen({ name: 'publish', draftId })}
          />
        )}
      </main>
      </div>

      {/* На телефоне пять кнопок внизу, остальное — в листе «Ещё».
          Восемь подписей в ряд наезжают друг на друга и не попадаются пальцем. */}
      {session && (
        <MobileNav
          freshOrders={freshOrders}
          screen={screen.name}
          isAdmin={Boolean(session.isAdmin)}
          isStaff={STAFF_DISPATCH.includes(session.staffRole ?? 'NONE')}
          user={session}
          onGo={goTab}
        />
      )}

      {/* Заявка — окном поверх экрана. Открывается из ленты, «моих заявок»,
          уведомлений и похожих; закрывается кнопкой, свайпом вниз, Escape
          и системной кнопкой «назад». */}
      <Modal
        open={cargoId !== null}
        onClose={() => setCargoId(null)}
        title="Заявка"
      >
        {cargoId && (
          <CargoDetail
            id={cargoId}
            onDone={() => setCargoId(null)}
            // Похожая заявка заменяет содержимое окна, а не открывает второе:
            // стопка окон — верный способ потерять, где ты находишься.
            onOpen={(next) => setCargoId(next)}
            onEdit={(editCargoId) => {
              setCargoId(null);
              setScreen({ name: 'publish', editCargoId });
            }}
          />
        )}
      </Modal>

      {/* Поверх всех экранов: искать помощь в меню человек не станет. */}
      <Support />
    </div>
    </MoneyProvider>
  );
}
